import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FLEET,
  FLEET_BY_KIND,
  cellsOf,
  randomPlacements,
  type Orientation,
  type Placement,
  type ShipKind,
} from '@bn/shared';
import { Alert, Badge, Button } from '@/components/ui';
import { Board, type CellRender } from './Board';
import { canPlace, occupiedCells } from './helpers';

interface Props {
  onSubmit: (placements: Placement[]) => Promise<void>;
  waiting: boolean;
}

export function PlacementBoard({ onSubmit, waiting }: Props) {
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [orientation, setOrientation] = useState<Orientation>('h');
  const [selected, setSelected] = useState<ShipKind>(FLEET[0]?.kind ?? 'carrier');
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const occupied = useMemo(() => occupiedCells(placements), [placements]);
  const placedKinds = useMemo(
    () => new Set(placements.map((p) => p.kind)),
    [placements],
  );
  const complete = placements.length === FLEET.length;

  const rotate = useCallback(() => {
    setOrientation((current) => (current === 'h' ? 'v' : 'h'));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'r') rotate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rotate]);

  const preview = useMemo(() => {
    if (!hover || waiting) return null;
    const candidate: Placement = { kind: selected, x: hover.x, y: hover.y, orientation };
    const size = FLEET_BY_KIND[selected].size;
    return {
      cells: new Set(cellsOf(candidate, size).map((c) => `${c.x},${c.y}`)),
      valid: canPlace(placements, candidate),
    };
  }, [hover, selected, orientation, placements, waiting]);

  function place(x: number, y: number) {
    const candidate: Placement = { kind: selected, x, y, orientation };
    if (!canPlace(placements, candidate)) {
      setError('Ce navire ne tient pas ici.');
      return;
    }
    setError(null);
    const next = [...placements.filter((p) => p.kind !== selected), candidate];
    setPlacements(next);

    const nextShip = FLEET.find((s) => !next.some((p) => p.kind === s.kind));
    if (nextShip) setSelected(nextShip.kind);
  }

  function renderCell(x: number, y: number): CellRender {
    const kind = occupied.get(`${x},${y}`);
    const inPreview = preview?.cells.has(`${x},${y}`) ?? false;

    if (inPreview) {
      return {
        className: preview?.valid
          ? 'bg-sky-400/70 ring-1 ring-sky-200'
          : 'bg-red-500/60 ring-1 ring-red-300',
        description: preview?.valid ? 'Position possible' : 'Position impossible',
        disabled: waiting,
      };
    }
    if (kind) {
      return {
        className: 'bg-slate-300 text-slate-900 hover:bg-slate-200',
        content: FLEET_BY_KIND[kind].label.charAt(0),
        description: FLEET_BY_KIND[kind].label,
        disabled: waiting,
      };
    }
    return {
      className: 'bg-sky-950/70 hover:bg-sky-900/80 ring-1 ring-inset ring-sky-900/60',
      description: 'Mer libre',
      disabled: waiting,
    };
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(placements);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <div>
        <Board
          renderCell={renderCell}
          onCellClick={waiting ? undefined : place}
          onCellEnter={(x, y) => setHover({ x, y })}
          onLeave={() => setHover(null)}
          dimmed={waiting}
        />
      </div>

      <div className="space-y-4">
        {waiting ? (
          <Alert type="info">
            Flotte validée. En attente de votre adversaire…
          </Alert>
        ) : (
          <p className="text-sm text-slate-400">
            Sélectionnez un navire, orientez-le avec la touche <kbd>R</kbd>, puis
            cliquez sur la grille.
          </p>
        )}

        <ul className="space-y-1.5">
          {FLEET.map((ship) => {
            const done = placedKinds.has(ship.kind);
            const active = selected === ship.kind;
            return (
              <li key={ship.kind}>
                <button
                  type="button"
                  disabled={waiting}
                  onClick={() => setSelected(ship.kind)}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                    active
                      ? 'border-sky-400 bg-sky-500/15 text-sky-100'
                      : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  <span>{ship.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{ship.size} cases</span>
                    {done && <Badge tone="succes">placé</Badge>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondaire" disabled={waiting} onClick={rotate}>
            Pivoter ({orientation === 'h' ? 'horizontal' : 'vertical'})
          </Button>
          <Button
            variant="secondaire"
            disabled={waiting}
            onClick={() => {
              setPlacements(randomPlacements());
              setError(null);
            }}
          >
            Placement aléatoire
          </Button>
          <Button
            variant="discret"
            disabled={waiting || placements.length === 0}
            onClick={() => {
              setPlacements([]);
              setSelected(FLEET[0]?.kind ?? 'carrier');
            }}
          >
            Tout effacer
          </Button>
        </div>

        {error && <Alert>{error}</Alert>}

        <Button
          className="w-full"
          disabled={!complete || busy || waiting}
          onClick={() => void submit()}
        >
          {busy ? 'Envoi…' : 'Valider ma flotte'}
        </Button>
      </div>
    </div>
  );
}
